/**
 * FmEntityCardGrid — object-card dashboard archetype shell.
 *
 * Owns the responsive grid of standalone ENTITY cards (e.g. the My Forests
 * property-home cards) so route roots never assemble FmCardGrid/FmCard
 * themselves. FmDashboardOverview reuses this same canonical entity-card
 * projection for its ranked owner records. An entity card is a whole-card link
 * to one domain object, with a status chip, a
 * meta line, a lead sentence, a value block, a job note, a primary action and
 * icon-only secondary actions — all owned + laid out by the archetype.
 *
 * A page passes TYPED entity-card descriptors (data + semantic variants + copy +
 * callbacks only). Every descriptor field is a string / number / semantic id /
 * callback — NEVER React.ReactNode, style, className, grid, or raw dimension.
 * The shell renders ALL Fm* nodes (FmCardGrid/FmCard/FmChip/FmMetaLine/FmText/
 * FmActionGroup/FmButton) internally, owns the whole-card link keyboard a11y,
 * and stops action-button clicks from also firing the card link.
 *
 * NO-FALLBACK: a value block renders exactly the string it is given; an
 * unavailable economic value is the caller's em-dash marker, never fabricated
 * here.
 */
import React from 'react';
import {
  AppstoreOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  MessageOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { FmCard } from './FmCard';
import { FmCardGrid, type FmCardGridMinColumn } from './FmCardGrid';
import { FmChip, type FmChipVariant } from './FmChip';
import { FmStack, FmText } from './FmComposition';
import { FmMetaLine, FmActionGroup, type FmMetaLineItem } from './FmListPrimitives';
import { FmButton } from './FmButton';
import type { FmActionConfirmationDescriptor } from './FmConfirm';
import { FmAssetImage } from './FmAssetImage';
import { FmPropertyBoundarySnapshot } from './FmPropertyBoundarySnapshot';
import { plainHeadingText } from './fmHostedHeading';
import { EntityCardManagement, EntityCardTitleText } from './FmEntityCardGrid.internal';
import { FmMapSnapshot } from '@/shared/ui/FmMapStage';
import { usePropertyForestSnapshotPublished } from '@/contexts/usePropertyForestSnapshot';
import './FmEntityCardGrid.css';
import {
  resolveContextualForestMediaResult,
  type ContextualForestMediaResolution,
  type ContextualForestMediaRequest,
} from '@/shared/config/contextualForestMedia';

/** Named semantic icon keys the entity card owns for its meta line. */
export type EntityMetaIconKey = 'location' | 'area';
/** Named semantic icon keys the entity card owns for its icon actions. */
export type EntityActionIconKey = 'overview' | 'manage' | 'assistant';

const META_ICONS: Record<EntityMetaIconKey, React.ReactNode> = {
  location: <EnvironmentOutlined />,
  area: <AppstoreOutlined />,
};

const ACTION_ICONS: Record<EntityActionIconKey, React.ReactNode> = {
  overview: <EyeOutlined />,
  manage: <SettingOutlined />,
  assistant: <MessageOutlined />,
};

/** A meta-line item (icon KEY + copy) rendered in the card subtitle. */
export interface EntityMetaDescriptor {
  id: string;
  iconKey?: EntityMetaIconKey;
  /** Meta label — i18n string, never JSX. */
  label: string;
}

/** Status chip descriptor rendered in the card header (extra slot). */
export interface EntityStatusChipDescriptor {
  /** Chip label — i18n string, never JSX. */
  label: string;
  variant?: FmChipVariant;
  testId?: string;
}

/** Localized media provenance copy supplied by the owning descriptor builder. */
export interface EntityMediaCueLabels {
  context: string;
  actual: string;
  illustrative: string;
  unavailable: string;
  emptyDescription: string;
}

/**
 * Value block: a quiet signal label, a pre-formatted value (NO-FALLBACK: the
 * caller passes an em-dash string when unavailable, never a fabricated number),
 * and a helper line.
 */
export interface EntityValueBlockDescriptor {
  /** Quiet label above the value — i18n string. */
  signalLabel?: string;
  /** Pre-formatted value (string/range/em-dash) or number, never JSX. */
  value: string | number;
  /** Helper copy under the value — i18n string. */
  helper?: string;
  valueTestId?: string;
}

/** The card's primary action (a labelled button). */
export interface EntityPrimaryActionDescriptor {
  id: string;
  /** Button label — i18n string, never JSX. */
  label: string;
  onClick: () => void;
  testId?: string;
}

export interface EntityCardRemoveActionDescriptor extends EntityPrimaryActionDescriptor {
  confirmation: FmActionConfirmationDescriptor;
  pending: boolean;
  error: string | null;
  confirmTestId?: string;
}

/** A secondary action: icon-only with an accessible name, except the assistant, which shows its label. */
export interface EntityIconActionDescriptor {
  id: string;
  iconKey: EntityActionIconKey;
  /** Accessible name / tooltip (the visible label for the assistant) — i18n string, never JSX. */
  ariaLabel: string;
  onClick: () => void;
  testId?: string;
}

/** A single entity card (all data-only; the shell renders every node). */
export interface EntityCardDescriptor {
  id: string;
  /** Card title (the object name) — i18n string, never JSX. */
  title: string;
  /** The title opens object details independently of the primary decision action. */
  titleAction?: EntityPrimaryActionDescriptor;
  /** Route-published geometry, rendered by the shared overview map capability. */
  propertyMap?: {
    propertyId: string;
    caption: string;
    /** Reports viewport entry and exit so the route can bound live map data. */
    onVisibilityChange?: (visible: boolean) => void;
  };
  /** Source-resolved context only; the archetype owns identity selection and crop. */
  mediaContext?: ContextualForestMediaRequest;
  /** Localized renderer copy for the resolved media mode. */
  mediaCueLabels?: EntityMediaCueLabels;
  /** Meta line (location · area …) rendered in the subtitle. */
  meta?: EntityMetaDescriptor[];
  /** Status chip in the header extra slot. */
  statusChip?: EntityStatusChipDescriptor;
  /** Lead sentence (plain-language state) — i18n string, never JSX. */
  leadSentence?: string;
  leadTestId?: string;
  /** Value block (signal label + value + helper). */
  valueBlock?: EntityValueBlockDescriptor;
  /** A quiet note line (e.g. the ranked job-to-do) — i18n string. */
  note?: string;
  noteTestId?: string;
  /** Primary labelled action. */
  primaryAction?: EntityPrimaryActionDescriptor;
  /** Destructive action exposed through the card's management menu. */
  removeAction?: EntityCardRemoveActionDescriptor;
  /** Secondary actions (icon-only, except the labelled assistant). */
  iconActions?: EntityIconActionDescriptor[];
  /** Stable test id for the actions row (preserves a route's action-group contract). */
  actionsTestId?: string;
  /** Whole-card link callback (Enter/Space + click). */
  onOpen?: () => void;
  /** Accessible name for the whole-card link — i18n string. */
  openAriaLabel?: string;
  testId?: string;
}

export interface FmEntityCardGridProps {
  /** Typed entity-card descriptors. The archetype owns the grid + cards. */
  cards: EntityCardDescriptor[];
  /** Minimum column width band (semantic, never px). */
  minColumn?: FmCardGridMinColumn;
  /** ARIA role for the grid container (for example, `list`). */
  role?: React.AriaRole;
  /** Semantic container for the grid. */
  as?: 'div' | 'section';
  /** ARIA label for the grid container. */
  ariaLabel?: string;
  'data-testid'?: string;
}

/** Wrap a card action so its click never also triggers the whole-card link. */
function stopAnd(handler: () => void) {
  return (event: React.SyntheticEvent) => {
    event.stopPropagation();
    handler();
  };
}

/** Build the whole-card link a11y props (or none when the card is static). */
function openLinkProps(card: EntityCardDescriptor) {
  if (!card.onOpen) return {};
  const onOpen = card.onOpen;
  return {
    role: 'link',
    tabIndex: 0,
    'aria-label': card.openAriaLabel,
    onClick: onOpen,
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onOpen();
      }
    },
  };
}

const EntityValueBlock: React.FC<{ block: EntityValueBlockDescriptor }> = ({ block }) => (
  <FmStack gap="xs">
    {block.signalLabel ? (
      <FmText size="caption" tone="subtle">
        {block.signalLabel}
      </FmText>
    ) : null}
    <FmText weight="medium" data-testid={block.valueTestId}>
      {block.value}
    </FmText>
    {block.helper ? (
      <FmText size="fine" tone="muted">
        {block.helper}
      </FmText>
    ) : null}
  </FmStack>
);

const EntityCardActions: React.FC<{ card: EntityCardDescriptor }> = ({ card }) => {
  const iconActions = card.iconActions ?? [];
  const managementAction = card.removeAction
    ? iconActions.find((action) => action.iconKey !== 'assistant')
    : undefined;
  const remainingIconActions = managementAction
    ? iconActions.filter((action) => action !== managementAction)
    : iconActions;
  if (!card.removeAction && iconActions.length === 0) return null;
  return (
    <FmActionGroup
      density="compact"
      collapseOnMobile
      data-testid={card.actionsTestId ?? `${card.testId ?? card.id}-actions`}
    >
      {card.removeAction ? (
        <EntityCardManagement
          managementAction={managementAction}
          removeAction={card.removeAction}
        />
      ) : null}
      {remainingIconActions.map((action) =>
        /* The assistant action sits alone under the card note, where a bare glyph
           reads as an orphan, so it carries its label the way the answer band's
           assistant action does. */
        action.iconKey === 'assistant' ? (
          <FmButton
            key={action.id}
            variant="text"
            leadingIcon={ACTION_ICONS[action.iconKey]}
            data-testid={action.testId}
            onClick={stopAnd(action.onClick)}
          >
            {action.ariaLabel}
          </FmButton>
        ) : (
          <FmButton
            key={action.id}
            leadingIcon={ACTION_ICONS[action.iconKey]}
            title={action.ariaLabel}
            aria-label={action.ariaLabel}
            data-testid={action.testId}
            onClick={stopAnd(action.onClick)}
          />
        ),
      )}
    </FmActionGroup>
  );
};

function contextualMediaCue(
  request: ContextualForestMediaRequest,
  resolution: ContextualForestMediaResolution | null,
  labels: EntityMediaCueLabels | undefined,
): string {
  const resolutionLabel =
    resolution?.mode === 'actual'
      ? labels?.actual
      : resolution?.mode === 'illustrative'
        ? labels?.illustrative
        : labels?.unavailable;
  return [
    labels?.context,
    request.basisLabel?.trim() || undefined,
    request.freshness,
    resolutionLabel,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Mount Leaflet only while the card is in the viewport or its route has
 * published the property's map data. The route reads and publishes a card's
 * data on visibility, so in the app both conditions coincide; a static render
 * (design-system pages, full-page captures) publishes its fixtures up front
 * and a viewport observer never reports cards below the fold. The unmounted
 * frame stays empty rather than showing a spinner: nothing is loading until the
 * card is shown. Cached query data and server/browser tile caches make
 * scroll-back cheap without retaining every map. A caption repeating the card
 * title prints the same id twice in one card, so it is dropped. */
const EntityPropertyMap: React.FC<{
  map: NonNullable<EntityCardDescriptor['propertyMap']>;
  cardTitle: string;
  testId: string;
}> = ({ map, cardTitle, testId }) => {
  const caption = plainHeadingText(map.caption) === plainHeadingText(cardTitle) ? undefined : map.caption;
  const host = React.useRef<HTMLDivElement>(null);
  const visibleRef = React.useRef(false);
  const onVisibilityChangeRef = React.useRef(map.onVisibilityChange);
  const [visible, setVisible] = React.useState(false);
  const published = usePropertyForestSnapshotPublished(map.propertyId);
  onVisibilityChangeRef.current = map.onVisibilityChange;
  React.useEffect(() => {
    const element = host.current;
    if (!element) return;
    const updateVisibility = (nextVisible: boolean) => {
      if (visibleRef.current === nextVisible) return;
      visibleRef.current = nextVisible;
      setVisible(nextVisible);
      onVisibilityChangeRef.current?.(nextVisible);
    };
    const observer = new IntersectionObserver((entries) => {
      const entry = entries.find((candidate) => candidate.target === element);
      if (entry) updateVisibility(entry.isIntersecting);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (visibleRef.current) {
        visibleRef.current = false;
        onVisibilityChangeRef.current?.(false);
      }
    };
  }, []);
  return (
    <div ref={host} data-property-id={map.propertyId}>
      {visible || published ? (
        <FmPropertyBoundarySnapshot
          propertyId={map.propertyId}
          caption={caption}
          size="compact"
          data-testid={testId}
        />
      ) : (
        <FmMapSnapshot size="compact" aspectRatio="wide" variant="media" caption={caption}>
          {null}
        </FmMapSnapshot>
      )}
    </div>
  );
};

const EntityCardBody: React.FC<{
  card: EntityCardDescriptor;
  mediaResult: ContextualForestMediaResolution | null;
  mediaCueLabels: EntityMediaCueLabels | undefined;
}> = ({ card, mediaResult, mediaCueLabels }) => {
  const mediaTestId = `${card.testId ?? card.id}-media`;
  return (
    <FmStack gap="md" className="fm-entity-card__content">
      {card.leadSentence ? (
        <FmText weight="semibold" data-testid={card.leadTestId}>
          {card.leadSentence}
        </FmText>
      ) : null}
      {card.primaryAction ? (
        <div className="fm-entity-card__primary">
          <FmButton
            variant="primary"
            size="lg"
            data-testid={card.primaryAction.testId}
            onClick={stopAnd(card.primaryAction.onClick)}
          >
            {card.primaryAction.label}
          </FmButton>
        </div>
      ) : null}
      {card.propertyMap ? (
        <EntityPropertyMap
          map={card.propertyMap}
          cardTitle={card.title}
          testId={`${card.testId ?? card.id}-map`}
        />
      ) : null}
      {card.mediaContext && mediaCueLabels ? (
        <FmStack gap="xs" data-testid={mediaTestId}>
          {mediaResult ? (
            <FmAssetImage assetKey={mediaResult.identity} cropRole="entity-card" />
          ) : (
            <FmText size="caption" tone="subtle" data-testid={`${mediaTestId}-empty`}>
              {mediaCueLabels.emptyDescription}
            </FmText>
          )}
          <FmText size="fine" tone="muted" data-testid={`${mediaTestId}-cue`}>
            {contextualMediaCue(card.mediaContext, mediaResult, mediaCueLabels)}
          </FmText>
        </FmStack>
      ) : null}
      {card.valueBlock ? <EntityValueBlock block={card.valueBlock} /> : null}
      {card.note ? (
        <FmText size="caption" tone="muted" data-testid={card.noteTestId}>
          {card.note}
        </FmText>
      ) : null}
      <EntityCardActions card={card} />
    </FmStack>
  );
};

const EntityCardTitle: React.FC<{ card: EntityCardDescriptor }> = ({ card }) =>
  card.titleAction ? (
    <FmButton
      variant="link"
      className="fm-entity-card__title-action"
      aria-label={card.titleAction.label}
      onClick={stopAnd(card.titleAction.onClick)}
      data-testid={card.titleAction.testId}
    >
      <EntityCardTitleText title={card.title} />
    </FmButton>
  ) : (
    <EntityCardTitleText title={card.title} />
  );

const EntityCard: React.FC<{ card: EntityCardDescriptor }> = ({ card }) => {
  if (card.mediaContext && !card.mediaCueLabels) {
    throw new Error(`Entity card "${card.id}" requires localized media cue labels.`);
  }
  const metaItems: FmMetaLineItem[] = (card.meta ?? []).map((item) => ({
    id: item.id,
    icon: item.iconKey ? META_ICONS[item.iconKey] : undefined,
    label: item.label,
  }));
  const mediaResult = card.mediaContext
    ? resolveContextualForestMediaResult(card.mediaContext)
    : null;
  return (
    <FmCard
      className="fm-entity-card"
      data-testid={card.testId}
      hoverable={Boolean(card.onOpen)}
      title={<EntityCardTitle card={card} />}
      subtitle={metaItems.length > 0 ? <FmMetaLine items={metaItems} /> : undefined}
      extra={
        card.statusChip ? (
          <FmChip variant={card.statusChip.variant} data-testid={card.statusChip.testId}>
            {card.statusChip.label}
          </FmChip>
        ) : undefined
      }
      {...openLinkProps(card)}
    >
      <EntityCardBody card={card} mediaResult={mediaResult} mediaCueLabels={card.mediaCueLabels} />
    </FmCard>
  );
};

/**
 * FmEntityCardGrid — the object-card grid archetype. Pages pass descriptors; the
 * archetype owns the grid, cards, chips, meta line, actions, and whole-card link.
 */
export const FmEntityCardGrid: React.FC<FmEntityCardGridProps> = ({
  cards,
  minColumn = 'md',
  as = 'div',
  role,
  ariaLabel,
  'data-testid': dataTestId = 'fm-entity-card-grid',
}) => (
  <FmCardGrid
    minColumn={minColumn}
    as={as}
    role={role}
    aria-label={ariaLabel}
    data-testid={dataTestId}
  >
    {cards.map((card) => (
      <EntityCard key={card.id} card={card} />
    ))}
  </FmCardGrid>
);

FmEntityCardGrid.displayName = 'FmEntityCardGrid';

export default FmEntityCardGrid;
